import { dataReviewApi } from '@/api/clients/dataReviewApi';
import {
  CcColumnsRequest as _CcColumnsRequest,
  CreateMealItemFoodMatchDetailsRequest,
  FoodResponse,
  ImLblMatchResult,
  MealItemCustomAddonResponse,
  MealPhotoQueueQualityAssuranceResponse,
  MealPhotoQueueResponse,
  NutrientOverrideCollection,
  PreparationMethodEnum,
  QualityAssuranceLogResponse,
  UsdaNutritionResponse,
} from '@/api/generated/MNT';
import { config } from '@/config';
import { logTrackedError } from '@/errorTracking';
import * as Sentry from '@/sentry';

import { type CoreRegionalApis, canadaCoreApis } from './coreRegionalApis';

export type MealQueueItem = MealPhotoQueueResponse;

export type DraftAddon = {
  id: number | null | undefined;
  // Client-only React key; `id` is null until the add-on is saved. See newClientKey.
  clientKey: string;
  // MNT returns rxfood_id on addons too, but the generated model doesn't declare
  // it. Widened so the addon link is visible to the type checker like the meal
  // item's is, instead of being silently untyped.
  item: MealItemCustomAddonResponse & Pick<MealItem, 'rxfood_id'> & Required<Pick<MealItem, 'rxfood_id_food_name'>>;
  searchItem: FoodResponse | null;
  queryText: string | null;
  foodMatchDetails: CreateMealItemFoodMatchDetailsRequest | null;
  imLblMatchResult?: ImLblMatchResult;
};

export type MealItem = {
  id?: number | null;
  addons: string[];
  cc_columns?: CcColumnsRequest;
  custom_item?: boolean | null;
  custom_usda_id?: number | null;
  food_name: string;
  food_name_alias?: string | null;
  food_replacement_name?: string | null;
  meal_photo_id?: number | null;
  note?: string | null;
  custom_nutrient_estimates?: Partial<NutrientEstimatesRequest>;
  nutrient_overrides?: Partial<NutrientOverrideCollection>;
  nutrition_source?: string | null;
  percent_eaten?: number;
  preparation_method?: PreparationMethodEnum;
  serving_type_label?: string | null;
  serving_type_multiplier?: number | null;
  serving_unit_amount: number;
  serving_unit_label: string;
  servings: number;
  custom_addons?: MealItemCustomAddonResponse[] | null; // aligns with backend
  draftCustomAddons?: DraftAddon[] | null; // augment custom_addons in line with draftItems
  custom_tip?: string | null;

  // Food-catalog link. MNT rejects the save if it doesn't match food_name.
  rxfood_id?: string | null;
  // Client-only: the food_name rxfood_id was resolved for. Stripped from the
  // top level of each request; MNT ignores unknown fields.
  rxfood_id_food_name?: string | null;

  // Set only when the reviewer logs this row from a recent item.
  source_meal_id?: number | null;
  source_meal_item_id?: number | null;
  source_meal_item_custom_addon_id?: number | null;

  carbohydrate_g?: number | null;
  netcarb_g?: number | null;
  energy_kcal?: number | null;
  protein_g?: number | null;
  fat_g?: number | null;
  fiber_g?: number | null;
  polyols_g?: number | null;

  last_updated_by_user_id?: number | null;
  patient_id?: number | null;

  custom_item_source?: string | null;
  custom_item_source_id?: string | null;

  food_ontology?: string[] | null;
};

export type CcColumnsRequest = _CcColumnsRequest;

export type NutrientEstimatesRequest = UsdaNutritionResponse;

export type QALog = QualityAssuranceLogResponse;

export const getMealQueueItem = async (item_id: number): Promise<MealQueueItem> => {
  const mealQueueItem = await dataReviewApi.appApiDataReviewerGetMealPhotoQueueItem({ meal_photo_queue_id: item_id });
  return mealQueueItem.data;
};

export const getMealQueueItemQaLogs = async (
  data_reviewer_id: number,
  item_id: number,
  apis: CoreRegionalApis = canadaCoreApis,
): Promise<QualityAssuranceLogResponse[]> => {
  const mealQaLogs = await apis.dataReviewApi.appApiQueueReviewGetQualityAssuranceLogs({
    data_reviewer_id,
    meal_photo_queue_id: item_id,
  });

  return mealQaLogs.data;
};

// Note: this is a hack used by the post-queue questionnaire to track whether the user has
// requested help.
export const onSendHelpCallback = {
  callback: null as null | ((helpMessage: string) => void),
};

export const sendHelpRequest = async (
  group_id: number,
  reviewer_id: number,
  queue_id: number,
  // Kept for existing callers; the region client attaches the region token.
  _access_token: string,
  helpMessage: string,
  apis: CoreRegionalApis = canadaCoreApis,
) => {
  onSendHelpCallback.callback?.(helpMessage);
  const path = `/api/data_reviewer_group/${group_id}/data_reviewer/${reviewer_id}/meal_photo_queue/${queue_id}/help`;
  const response = await apis.fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message: helpMessage }),
  });
  console.log(response);

  if (response.status !== 200) {
    logTrackedError({
      sourceName: 'sendHelpRequest',
      origin: response,
      stackError: new Error(),
      context: {
        reviewer_id,
        queue_id,
        helpMessage,
      },
      userMessage: 'Error submitting help request. Please use alternative means to obtain assistance.',
    });
    return false;
  }

  return true;
};

export const submitExpertContext = async (
  reviewer_id: number,
  queue_id: number,
  note: string,
  apis: CoreRegionalApis = canadaCoreApis,
): Promise<boolean> => {
  try {
    await apis.dataReviewApi.appApiDataReviewerPutExpertContextAndRestartPrelabeling({
      data_reviewer_id: reviewer_id,
      meal_photo_queue_id: queue_id,
      ExpertContextRequest: { note },
    });
    return true;
  } catch (err) {
    logTrackedError({
      sourceName: 'mpq.submitExpertContext',
      origin: err as any,
      stackError: new Error(),
      context: { reviewer_id, queue_id, note },
      userMessage: 'Could not submit expert context note. Please try again.',
    });
    return false;
  }
};

export const getQueuesForQa = async (
  access_token: string,
  params: {
    data_reviewer_id: number;
    date_oldest?: string;
    date_newest?: string;
    is_reviewed?: string;
    is_approved?: string;
    is_escalated?: string;
    is_auto_logged?: string;
    is_priority?: string;
    is_initial?: string;
    has_custom_items?: string;
    patient_id?: number;
    limit?: number;
    is_from_recent?: string;
  },
): Promise<MealPhotoQueueQualityAssuranceResponse[]> => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([name, value]) => {
    if (name === 'data_reviewer_id') {
      return;
    }
    if (value === null || value === undefined || value === '') {
      return;
    }
    q.append(name, value.toString());
  });
  const url = `${config.API_URL}/api/data_reviewer/${params.data_reviewer_id}/quality_assurance?${q.toString()}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${access_token}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
    mode: 'cors',
  });

  if (response.status !== 200) {
    throw new Error(`${response.status}: ${await response.text()}`);
  }

  return response.json();
};

export const getFeedback = async (
  access_token: string,
  params: {
    data_reviewer_id: number;
    perPage: number;
    highestId: number | null;
  },
): Promise<QALog[]> => {
  const q = new URLSearchParams();
  q.append('per_page', params.perPage.toString());

  if (params.highestId != null && Number.isFinite(params.highestId)) {
    q.append('cursor', params.highestId.toString());
  }

  const url = `${config.API_URL}/api/data_reviewer/${params.data_reviewer_id}/unacknowledged_logs?${q.toString()}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${access_token}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
    mode: 'cors',
  });

  if (response.status !== 200) {
    throw new Error(`${response.status}: ${await response.text()}`);
  }

  return response.json();
};

export const acknowledgeFeedback = async (
  access_token: string,
  params: {
    data_reviewer_id: number;
    item_id: number;
  },
): Promise<QALog[]> => {
  const url = `${config.API_URL}/api/data_reviewer/${params.data_reviewer_id}/logs/${params.item_id}/acknowledge`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${access_token}`,
      'Content-Type': 'application/json',
    },
    method: 'PUT',
    mode: 'cors',
  });

  if (response.status !== 200) {
    throw new Error(`${response.status}: ${await response.text()}`);
  }

  return response.json();
};
